import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { getInvalidDxfFiles, fixDxfInsunits } from "../utils/dxfInsunits";

/**
 * Stable unique identifier for a File object within a single session.
 * Uses name + size + lastModified to distinguish files with the same name
 * uploaded from different folders.
 *
 * @param {File} file
 * @returns {string}
 */
const fileId = (file) => `${file.name}-${file.size}-${file.lastModified}`;

/**
 * Encapsulates DXF $INSUNITS validation state and fix logic.
 * Follows the useShipping pattern — caller passes doUpload so this hook
 * stays free of API/localStorage concerns.
 *
 * @param {(files: File[]) => Promise<void>} doUpload
 */
export default function useDxfInsunitsValidation(doUpload) {
  // Array of { id: string, name: string } for each invalid file.
  // Using a unique id (not just name) to correctly handle files that share
  // the same filename but come from different folders.
  const [invalidFileNames, setInvalidFileNames] = useState([]);
  const [pendingFilesForFix, setPendingFilesForFix] = useState([]);
  // Keyed by fileId, not file.name, to avoid collisions.
  const [dimensionTypeByFile, setDimensionTypeByFile] = useState({});

  const setDimensionForFile = useCallback((id, value) => {
    setDimensionTypeByFile((prev) => ({ ...prev, [id]: Number(value) }));
  }, []);

  const resetInsunits = useCallback(() => {
    setInvalidFileNames([]);
    setPendingFilesForFix([]);
    setDimensionTypeByFile({});
  }, []);

  /**
   * Validates files on drop. Returns true if all files are valid (caller
   * uploads them). Returns false when INSUNITS issues need the fix UI, or
   * when binary files are rejected (binary files are never uploaded —
   * dxfgrabber is ASCII-only and cannot parse them).
   */
  const validateOnDrop = useCallback(
    async (acceptedFiles) => {
      resetInsunits();
      const { invalidFiles, binaryFiles } = await getInvalidDxfFiles(acceptedFiles);

      if (binaryFiles.length > 0) {
        toast.error(
          `Binary DXF format is not supported. Re-save as ASCII DXF in your CAD software: ${binaryFiles.map((f) => f.name).join(', ')}`
        );
      }

      // Binary files are never included in the fix flow or upload.
      const nonBinaryFiles = acceptedFiles.filter((f) => !binaryFiles.includes(f));

      if (invalidFiles.length > 0) {
        const fileData = invalidFiles.map((f) => ({ id: fileId(f), name: f.name }));
        setInvalidFileNames(fileData);
        setPendingFilesForFix(nonBinaryFiles);
        setDimensionTypeByFile(
          fileData.reduce((acc, { id }) => ({ ...acc, [id]: 1 }), {})
        );
        return false;
      }

      if (binaryFiles.length > 0) {
        if (nonBinaryFiles.length > 0) {
          await doUpload(nonBinaryFiles);
        }
        return false;
      }

      return true;
    },
    [resetInsunits, doUpload]
  );

  const handleFixFiles = useCallback(
    async () => {
      if (pendingFilesForFix.length === 0) return;

      const invalidIds = new Set(invalidFileNames.map(({ id }) => id));
      const fixedFiles = [];
      const skippedFiles = [];

      for (const file of pendingFilesForFix) {
        if (invalidIds.has(fileId(file))) {
          const fixed = await fixDxfInsunits(
            file,
            dimensionTypeByFile[fileId(file)] ?? 1
          );
          if (fixed) {
            fixedFiles.push(fixed);
          } else {
            skippedFiles.push(file.name);
          }
        } else {
          fixedFiles.push(file);
        }
      }

      if (skippedFiles.length > 0) {
        toast.warning(
          `${skippedFiles.length} file(s) could not be fixed and were skipped: ${skippedFiles.join(", ")}`
        );
      }
      if (fixedFiles.length === 0) {
        toast.error("None of the files could be fixed. Please check your files and try again.");
      }

      resetInsunits();
      if (fixedFiles.length > 0) {
        await doUpload(fixedFiles);
      }
    },
    [
      pendingFilesForFix,
      invalidFileNames,
      dimensionTypeByFile,
      resetInsunits,
      doUpload,
    ]
  );

  return {
    invalidFileNames,
    dimensionTypeByFile,
    setDimensionForFile,
    validateOnDrop,
    handleFixFiles,
    resetInsunits,
  };
}
